import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@/stores/hooks';
import { setWorkspaceContext } from '@/stores/sideMenuSlice';
import { getEncryptedClientId } from '@/utils/helper';
import { isNonClientRoute } from '@/utils/nonClientRoutes';
import { WorkspaceContext } from '@/utils/workSpaceContext';


export function isChatRootPath(pathname: string): boolean {
  const encryptedClientId = getEncryptedClientId();
  if (!encryptedClientId) return false;
  const segments = pathname.split('/').filter(Boolean);

  // /:clientId  (new/unsaved chat)
  if (segments.length === 1 && segments[0] === encryptedClientId) return true;

  // /:clientId/chat/:chatId  (a specific saved chat)
  if (
    segments.length === 3 &&
    segments[0] === encryptedClientId &&
    segments[1] === 'chat'
  ) {
    return true;
  }

  return false;
}

function deriveWorkspaceContextFromPath(pathname: string): WorkspaceContext | null {
  if (isNonClientRoute(pathname)) return null;

  const encryptedClientId = getEncryptedClientId();
  if (!encryptedClientId) return null;

  if (isChatRootPath(pathname)) {
    return { kind: 'intelligence' };
  }

  const segments = pathname.split('/').filter(Boolean);

  // /:encryptedId/agents/:agentId
  if (segments.length >= 3 && segments[0] === encryptedClientId && segments[1] === 'agents') {
    return { kind: 'agent', agentId: segments[2] };
  }

  return null;
}

export function useSyncWorkspaceContextFromUrl() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const lastSyncedPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastSyncedPath.current === location.pathname) return;
    lastSyncedPath.current = location.pathname;

    const context = deriveWorkspaceContextFromPath(location.pathname);
    if (context) {
      dispatch(setWorkspaceContext(context));
    }
  }, [location.pathname, dispatch]);
}